import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { VideoDialog } from '@/components/common/VideoDialog'
import { HlsVideoPlayer } from '@/components/common/HlsVideoPlayer'
import {
  fetchNewsList,
  parseNewsTitle,
  stripHtml,
  type QuebonNewsItem,
} from '@/api/quebonNews'

interface MediaVideo {
  name: string
  shortLabel: string
  label: string
  thumbnail: string
  src: string
}

interface MediaSectionProps {
  title: string
  titleHighlight: string
  videos: MediaVideo[]
  moreLabel: string
  moreUrl: string
}

const PLAY_ICON =
  'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section04/icon-player.svg'

function NewsItem({
  source,
  title,
  excerpt,
  href,
}: {
  source: string
  title: string
  excerpt: string
  href: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="flex w-full py-2 hover:opacity-80 transition-opacity"
    >
      <div className="flex flex-col gap-2 w-full">
        {source && (
          <div className="font-bold text-[16px] leading-[1.5] tracking-[-0.6px] text-qb-primary w-[122px] shrink-0">
            {source}
          </div>
        )}
        <h3 className="font-semibold text-[18px] leading-[1.5] tracking-[-0.6px] text-qb-text-high m-0 truncate">
          {title}
        </h3>
        <p className="font-medium text-[16px] leading-[1.5] text-qb-text-low m-0 line-clamp-1 overflow-hidden">
          {excerpt}
        </p>
      </div>
    </a>
  )
}

export function MediaSection({
  title,
  titleHighlight,
  videos,
  moreLabel,
  moreUrl,
}: MediaSectionProps) {
  const { t: mt, i18n } = useTranslation('marketing')

  const [selectedName, setSelectedName] = useState(videos[0].name)
  const selected = videos.find(video => video.name === selectedName) ?? videos[0]
  const [openVideo, setOpenVideo] = useState<MediaVideo | null>(null)
  const [news, setNews] = useState<QuebonNewsItem[]>([])

  useEffect(() => {
    let cancelled = false
    fetchNewsList(0, 3)
      .then((res) => {
        if (!cancelled) setNews(res.list || [])
      })
      .catch((e) => {
        console.error('Failed to load news', e)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const articles = news.slice(0, 3).map((item) => {
    const parsed = parseNewsTitle(item.title)
    return {
      id: item.noticeId,
      source: parsed.source,
      title: parsed.title,
      excerpt: item.content ? stripHtml(item.content) : '-',
    }
  })

  return (
    <section id="media" data-marketing-section="" className="w-full bg-qb-bg">
      <div className="mx-auto max-w-[1136px] px-6 lg:px-10 xl:px-0 py-14 lg:py-[104px]">
        {/* Title */}
        <div className="flex flex-col items-center gap-2 lg:gap-4 mb-14 lg:mb-20">
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-text-high text-center">
            {title}
          </h2>
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-primary text-center">
            {titleHighlight}
          </h2>
        </div>

        {/* Content */}
        <div data-testid="text-image-split" className="flex flex-col lg:flex-row gap-10">
          {/* Media (left) */}
          <div className="flex flex-col gap-6 w-full lg:w-[594px] shrink-0">
            <button
              type="button"
              onClick={() => setOpenVideo(selected)}
              className="relative w-full lg:h-[367px] aspect-video rounded-3xl overflow-hidden shadow-[0_8px_16px_rgba(0,0,0,0.2)] cursor-pointer group"
              aria-label={mt('media.play', { title: selected.label })}
            >
              <img
                src={selected.thumbnail}
                alt={selected.label}
                className="w-full h-full object-cover"
              />
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 lg:w-22 lg:h-22 rounded-full bg-cover bg-center bg-no-repeat backdrop-blur-md group-hover:scale-105 transition-transform"
                style={{
                  backgroundImage: `url(${PLAY_ICON})`,
                  width: '88px',
                  height: '88px',
                }}
              />
            </button>

            {/* Tabs */}
            <div className="marketing-media-tabs flex gap-4 items-center justify-center">
              {videos.map((v) => {
                const active = selected.name === v.name
                return (
                  <button
                    key={v.name}
                    type="button"
                    onClick={() => setSelectedName(v.name)}
                    className={`
                      h-12 lg:h-12 px-6 rounded-full font-medium text-[16px] lg:text-[18px] leading-[1.5] tracking-[-0.6px] transition-all whitespace-nowrap
                      ${active
                        ? 'bg-qb-primary text-white'
                        : 'bg-qb-bg-secondary text-qb-text-mid hover:bg-qb-gray-100'
                      }
                    `}
                  >
                    <span className="hidden lg:inline">{v.label}</span>
                    <span className="lg:hidden">{v.shortLabel}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Divider — desktop only */}
          <div className="hidden lg:block w-px self-stretch bg-qb-divider shrink-0" />

          {/* News (right) */}
          <div className="flex flex-col justify-between items-end gap-6 flex-1 w-full min-w-0">
            <div className="flex flex-col gap-6 w-full min-w-0">
              {i18n.resolvedLanguage === 'en' && <p className="text-sm text-qb-text-mid">{mt('pressKoreanOnly')}
              </p>}
              {articles.length === 0 && (
                <p className="text-qb-text-low text-[14px]">
                  {mt('MediaSection.copy357_357')}
                </p>
              )}
              {articles.map((a) => (
                <NewsItem
                  key={a.id}
                  source={a.source}
                  title={a.title}
                  excerpt={a.excerpt}
                  href={`https://www.quebon.tv/quebon-news/news/${a.id}`}
                />
              ))}
            </div>

            <a
              href={moreUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2 lg:gap-2 pl-4 hover:translate-x-1 transition-transform"
            >
              <span className="font-semibold text-[16px] lg:text-[18px] leading-[1.5] tracking-[-0.6px] text-qb-primary">
                {moreLabel}
              </span>
              <ChevronRight className="w-4 h-4 lg:w-6 lg:h-6 text-qb-primary" />
            </a>
          </div>
        </div>
      </div>

      <VideoDialog
        open={openVideo !== null}
        onOpenChange={(open) => {
          if (!open) setOpenVideo(null)
        }}
      >
        {openVideo && (
          <HlsVideoPlayer
            key={openVideo.src}
            src={openVideo.src}
            poster={openVideo.thumbnail}
          />
        )}
      </VideoDialog>
    </section>
  )
}
